// Visitor invite wizard — RON-5466 Phase 2. Prototype: `InviteStep1`–`InviteStep4`.
//
// Owns the whole form in one state object and delegates each step to its own component, so no
// step needs a dozen individual setters. Steps are indices into STEP_KEYS.
//
// NOT BUILT: the prototype's `InviteType` chooser ("Invite someone" — pick Visitor or
// Contractor). It is unreachable in the prototype too: the list's "+ Invite visitor" navigates
// straight to `inviteStep1` and "+ Invite contractor" to `cStep1`, and nothing ever sets
// `screen === 'inviteType'`. Our two list buttons do the same, so the chooser has no entry point.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EMAIL_PATTERN } from "../../../utils/CommonUtils.js";
import { VISIT_KIND, VISIT_TYPE } from "../visitorConstants.js";
import { isSameOrBefore } from "../visitorDateUtils.js";
import VisitDetailsStep from "./VisitDetailsStep.jsx";
import VisitorDetailsStep, { EMPTY_VISITOR } from "./VisitorDetailsStep.jsx";
import VisitorReviewStep from "./VisitorReviewStep.jsx";
import VisitorStepBar from "./VisitorStepBar.jsx";
import VisitorTypeStep from "./VisitorTypeStep.jsx";

const STEP_KEYS = [
  "react_visitors_step_details",
  "react_visitors_step_visit",
  "react_visitors_step_type",
  "react_visitors_step_review",
];

const STEP = { DETAILS: 0, VISIT: 1, TYPE: 2, REVIEW: 3 };

// Defaults chosen to be immediately submittable: today, a normal working window.
//
// `DEFAULT_EXPECTED_OUT` was here until 07-October-2026. `expected_out` is gone from the
// invite flows by product decision — see `VisitDetailsStep.jsx` for the full note.
const DEFAULT_ARRIVAL = "09:00";

const defaultBuildingId = (site) => {
  if (!site?.buildings?.length) return null;

  const preferred = site.buildings.find((building) => building.isDefault);

  return (preferred || site.buildings[0]).id;
};

const VisitorInviteWizard = ({
  sites,
  todayISO,
  hostName,
  canAssignDesk,
  canAssignParking,
  isSubmitting,
  onSubmit,
  onCancel,
  // Phase 3: the same wizard edits an existing visit. Reused rather than duplicated so the two
  // can never validate differently — an edit screen that accepted what the invite screen rejected
  // would be a slow-burning data problem.
  mode = "create",
  initialVisit = null,
}) => {
  const { t } = useTranslation("common");

  const isEdit = mode === "edit" && Boolean(initialVisit);

  const [step, setStep] = useState(STEP.DETAILS);
  const [visitors, setVisitors] = useState(() =>
    // A visit row IS one guest (db-schema §5), so editing one edits exactly one person.
    isEdit
      ? [
          {
            name: initialVisit.name || "",
            email: initialVisit.email || "",
            company: initialVisit.company || "",
          },
        ]
      : [{ ...EMPTY_VISITOR }]
  );
  // Editing defaults to NOT re-sending the invite: the guest already has one, and a second copy
  // for a changed arrival time should be a deliberate choice.
  const [sendEmail, setSendEmail] = useState(!isEdit);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState(() =>
    isEdit
      ? {
          startDate: initialVisit.startDate,
          endDate: initialVisit.endDate,
          arrivalTime: initialVisit.arrivalTime,
          // Resolved from names once `sites` lands — see below.
          siteId: null,
          buildingId: null,
          host: initialVisit.host || hostName,
          type: initialVisit.type || VISIT_TYPE.STANDARD,
          resources: {},
        }
      : {
          startDate: todayISO,
          endDate: todayISO,
          arrivalTime: DEFAULT_ARRIVAL,
          siteId: null,
          buildingId: null,
          host: hostName,
          type: VISIT_TYPE.STANDARD,
          resources: {},
        }
  );

  // Sites arrive asynchronously; select the first one (and its default building) once they land.
  // Creating only — an edit must not have its location silently replaced by the first site.
  useEffect(() => {
    if (isEdit || !sites.length || form.siteId) return;

    const first = sites[0];
    setForm((current) => ({
      ...current,
      siteId: first.id,
      buildingId: defaultBuildingId(first),
    }));
  }, [isEdit, sites, form.siteId]);

  // A visit stores its site and building by NAME — that is what the list and detail screens
  // display — while the wizard works in ids. Bridge the two once sites are available.
  useEffect(() => {
    if (!isEdit || !sites.length || form.siteId) return;

    const site = sites.find((candidate) => candidate.name === initialVisit.site) || null;
    const building =
      site?.buildings?.find((candidate) => candidate.name === initialVisit.building) ||
      null;

    setForm((current) => ({
      ...current,
      // A site that no longer exists leaves the picker empty rather than guessing a different
      // one, so the host has to choose deliberately.
      siteId: site?.id ?? null,
      buildingId: building?.id ?? (site ? defaultBuildingId(site) : null),
    }));
  }, [isEdit, initialVisit, sites, form.siteId]);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === form.siteId) || null,
    [sites, form.siteId]
  );

  const selectedBuilding = useMemo(
    () =>
      selectedSite?.buildings?.find((building) => building.id === form.buildingId) || null,
    [selectedSite, form.buildingId]
  );

  const setField = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };

      // Keep the range valid: moving the start past the end drags the end with it.
      if (key === "startDate" && !isSameOrBefore(value, next.endDate)) {
        next.endDate = value;
      }

      // Changing site resets the building to that site's default — a building must belong to its
      // site (db-schema §0.3), so carrying the old one over would be invalid.
      if (key === "siteId") {
        const site = sites.find((candidate) => candidate.id === value) || null;
        next.buildingId = defaultBuildingId(site);
      }

      return next;
    });

    setErrors({});
  };

  const updateVisitor = (index, key, value) => {
    setVisitors((current) =>
      current.map((visitor, position) =>
        position === index ? { ...visitor, [key]: value } : visitor
      )
    );
    setErrors({});
  };

  const addVisitor = () => setVisitors((current) => [...current, { ...EMPTY_VISITOR }]);

  const removeVisitor = (index) =>
    setVisitors((current) => current.filter((_, position) => position !== index));

  // Validation runs per step, so a user is never blocked by a field they have not reached.
  const validateStep = (candidate) => {
    if (candidate === STEP.DETAILS) {
      const visitorErrors = {};

      visitors.forEach((visitor, index) => {
        const fieldErrors = {};

        if (!visitor.name.trim()) {
          fieldErrors.name = "react_visitors_error_name";
        }
        if (!visitor.email.trim()) {
          fieldErrors.email = "react_visitors_error_email_required";
        } else if (!EMAIL_PATTERN.test(visitor.email.trim())) {
          // Same pattern the login and booking screens use, so validation is consistent.
          fieldErrors.email = "react_visitors_error_email_invalid";
        }

        if (Object.keys(fieldErrors).length) visitorErrors[index] = fieldErrors;
      });

      return Object.keys(visitorErrors).length ? { visitors: visitorErrors } : null;
    }

    if (candidate === STEP.VISIT) {
      const visitErrors = {};

      if (!form.host.trim()) visitErrors.host = "react_visitors_error_host";

      return Object.keys(visitErrors).length ? { visit: visitErrors } : null;
    }

    return null;
  };

  const goNext = () => {
    const found = validateStep(step);

    if (found) {
      setErrors(found);
      return;
    }

    setStep((current) => Math.min(current + 1, STEP.REVIEW));
  };

  // Back from the first step leaves the wizard entirely.
  const goBack = () => {
    if (step === STEP.DETAILS) {
      onCancel();
      return;
    }

    setErrors({});
    setStep((current) => Math.max(current - 1, STEP.DETAILS));
  };

  const submit = () => {
    onSubmit({
      kind: initialVisit?.kind || VISIT_KIND.VISITOR,
      // Carried so the caller can PATCH the right row instead of matching on name.
      visitId: initialVisit?.id ?? null,
      visitors: visitors.map((visitor) => ({
        name: visitor.name.trim(),
        email: visitor.email.trim(),
        company: visitor.company.trim(),
      })),
      site: selectedSite,
      building: selectedBuilding,
      sendEmail,
      ...form,
    });
  };

  const renderStep = () => {
    if (step === STEP.DETAILS) {
      return (
        <VisitorDetailsStep
          visitors={visitors}
          errors={errors.visitors}
          onChange={updateVisitor}
          onAdd={addVisitor}
          onRemove={removeVisitor}
          // One visit row is one guest, so an edit has nobody to add or remove.
          allowMultiple={!isEdit}
        />
      );
    }

    if (step === STEP.VISIT) {
      return (
        <VisitDetailsStep
          form={form}
          sites={sites}
          errors={errors.visit}
          onField={setField}
          canAssignDesk={canAssignDesk}
          canAssignParking={canAssignParking}
        />
      );
    }

    if (step === STEP.TYPE) {
      return (
        <VisitorTypeStep
          value={form.type}
          onChange={(type) => setField("type", type)}
        />
      );
    }

    return (
      <VisitorReviewStep
        form={form}
        visitors={visitors}
        site={selectedSite}
        building={selectedBuilding}
        sendEmail={sendEmail}
        onToggleEmail={() => setSendEmail((current) => !current)}
        canAssignDesk={canAssignDesk}
        canAssignParking={canAssignParking}
      />
    );
  };

  return (
    <div className="visitor-wizard">
      <h2 className="module-title mb-3">
        {t(isEdit ? "react_visitors_edit_visitor" : "react_visitors_invite_a_visitor")}
      </h2>

      <VisitorStepBar steps={STEP_KEYS} current={step} />

      {renderStep()}

      <div className="visitor-wizard__actions">
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={goBack}
          disabled={isSubmitting}
        >
          {step === STEP.DETAILS
            ? t("react_visitors_back_to_list")
            : t("react_visitors_back")}
        </button>

        {step === STEP.REVIEW ? (
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={submit}
            disabled={isSubmitting}
          >
            {t(isEdit ? "react_visitors_save_changes" : "react_visitors_submit")}
          </button>
        ) : (
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={goNext}
          >
            {t("react_visitors_next")}
          </button>
        )}
      </div>
    </div>
  );
};

export default VisitorInviteWizard;
